import CircularLoader, {
  CircularLoaderType,
} from '@allesauto/core/circularLoader';
import translate from '@allesauto/i18n';
import {
  useAccessoriesInCategory,
  useAccessoriesTop,
} from '@allesauto/utils/apiHooks';
import { ToastifyType } from '@allesauto/utils/consts';
import clsx from 'clsx';
import React, { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Footer from '@/components/OverlayFormFooter/OverlayFormFooter';

import { closeOverlay } from '@/store/redux/actions/app';

import AccessoriesCatalogItem from './AccessoriesCatalogItem';
import CategoryDropdown from './CategoryDropdown';
import AccessoriesDetails from '../Details/AccessoriesDetails';

interface AccessoriesCatalogProps {
  accessoriesDealerProviderId: string;
  vehicleVin?: string;
  vehicleSeries?: string;
}

const AccessoriesCatalog: FC<
  React.PropsWithChildren<AccessoriesCatalogProps>
> = ({ accessoriesDealerProviderId, vehicleVin, vehicleSeries }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>();
  const [selectedProductId, setSelectedProductId] = useState<string>();

  const commonFetchParams = {
    dealerId: accessoriesDealerProviderId,
    vin: vehicleVin ?? undefined,
    series: vehicleSeries ?? undefined,
  };

  const {
    result: topsResult,
    loading: topsLoading,
    error: topsError,
  } = useAccessoriesTop(commonFetchParams, {
    skip: !!selectedCategoryId,
  });

  const {
    result: inCategoryResult,
    loading: inCategoryLoading,
    error: inCategoryError,
  } = useAccessoriesInCategory(
    {
      ...commonFetchParams,
      categoryId: selectedCategoryId ?? '',
    },
    {
      skip: !selectedCategoryId,
    }
  );

  const productsLoading = topsLoading || inCategoryLoading;
  const products = (selectedCategoryId ? inCategoryResult : topsResult) ?? [];

  useEffect(() => {
    if (topsError || inCategoryError) {
      import('@allesauto/core/toastify').then((module) => {
        module.toastifyFactory(
          ToastifyType.ERROR,
          translate(intl, 'global.ERRORS.DEFAULT.HEADER'),
          translate(intl, 'global.ERRORS.DEFAULT.TEXT')
        );
      });
    }
  }, [intl, topsError, inCategoryError]);

  const className = {
    container: 'h-full',
    loader: 'flex items-center justify-center mt-100px',
    categories:
      'mb-30px md:mb-40px grid grid-cols-1 md:grid-cols-2 gap-x-60px gap-y-10px',
    contentWrapper: 'pb-[160px]',
    grid: 'grid grid-cols-1 md:grid-cols-2 gap-x-60px gap-y-30px md:gap-y-40px',
  };

  return (
    <div className={className.container}>
      {!!selectedProductId && (
        <AccessoriesDetails
          accessoriesDealerProviderId={accessoriesDealerProviderId}
          productId={selectedProductId}
          backToCatalog={() => setSelectedProductId(undefined)}
        />
      )}

      <div className={clsx(!!selectedProductId && 'hidden')}>
        <div className={className.categories}>
          <CategoryDropdown
            fetchParams={commonFetchParams}
            onSelect={(v) => setSelectedCategoryId(v)}
          />
        </div>

        <div className={className.contentWrapper}>
          {productsLoading ? (
            <div className={className.loader}>
              <CircularLoader type={CircularLoaderType.secondary} />
            </div>
          ) : (
            <div className={className.grid}>
              {products.map((p) => (
                <AccessoriesCatalogItem
                  key={p.productId}
                  product={p}
                  onClick={(productId) => setSelectedProductId(productId)}
                />
              ))}
            </div>
          )}
        </div>

        <Footer
          buttonText={translate(
            intl,
            'ui.SHOPPING_CART.ACCESSORIES.CLOSE_OVERLAY'
          )}
          onButtonClick={() => dispatch(closeOverlay())}
        />
      </div>
    </div>
  );
};

export default AccessoriesCatalog;
